import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Rule } from 'antd/es/form';
import { validateInput } from '../../../utils/textUtils';

/**
 * Custom hook for common form validation rules
 * 
 * Provides reusable validation rule factories to eliminate duplication
 * across expense form components.
 */
export const useFormValidation = () => {
  const { t } = useTranslation();

  /**
   * Creates validation rules for required fields with text validation
   * 
   * @param fieldName - The field name for error messages (will be lowercased)
   * @param options - Additional validation options
   * @returns Array of validation rules
   */
  const createRequiredFieldRules = useMemo(() => (
    fieldName: string,
    options: {
      /** Whether the field is required (default: true) */
      required?: boolean;
      /** Whether to apply text validation (default: true) */
      validateText?: boolean;
      /** Custom required message override */
      customRequiredMessage?: string;
    } = {}
  ) => {
    const { required = true, validateText = true, customRequiredMessage } = options;
    
    const rules: Rule[] = [];

    // Required field rule
    if (required) {
      rules.push({
        required: true,
        message: customRequiredMessage || t('Please enter the field!', {
          field: t(fieldName).toLocaleLowerCase(),
        }),
      });
    }

    // Text validation rule
    if (validateText) {
      rules.push(() => ({
        validator(_: any, value: any) {
          return validateInput(value, t);
        },
      }));
    }

    return rules;
  }, [t]);

  /**
   * Creates validation rules for required fields without text validation
   * 
   * @param fieldName - The field name for error messages (will be lowercased)
   * @param customMessage - Optional custom error message
   * @returns Array of validation rules
   */
  const createRequiredOnlyRules = useMemo(() => (
    fieldName: string,
    customMessage?: string
  ) => {
    return [
      {
        required: true,
        message: customMessage || t('Please enter the field!', {
          field: t(fieldName).toLocaleLowerCase(),
        }),
      },
    ];
  }, [t]);

  /**
   * Creates validation rules for select fields (dropdowns)
   * 
   * @param fieldName - The field name for error messages
   * @param required - Whether the field is required (default: true)
   * @returns Array of validation rules
   */
  const createSelectFieldRules = useMemo(() => (
    fieldName: string,
    required: boolean = true
  ) => {
    if (!required) return [];
    
    return [
      {
        required: true,
        message: t('Please select the') + ' ' + t(fieldName) + '!',
      },
    ];
  }, [t]);

  /**
   * Creates conditional validation rules based on settings
   * 
   * @param fieldName - The field name for error messages
   * @param condition - Whether the field is required
   * @returns Array of validation rules
   */
  const createConditionalRules = useMemo(() => (
    fieldName: string,
    condition: boolean
  ) => {
    return [
      {
        required: condition,
        message: t('Please enter the field!', {
          field: t(fieldName).toLocaleLowerCase(),
        }),
      },
    ];
  }, [t]);

  return {
    createRequiredFieldRules,
    createRequiredOnlyRules,
    createSelectFieldRules,
    createConditionalRules,
  };
};